import { useState, useCallback, useRef, useEffect } from 'react';
import { stripTypes } from '../runtime/type-stripper';
import { getXionMocks } from '../runtime/xion-mock';
import './Console.css';

export interface ConsoleEntry {
  type: 'log' | 'error' | 'warn' | 'info' | 'result';
  content: string;
  timestamp: number;
}

interface ConsoleProps {
  tsCode: string;  // TypeScript code from transpiler
  onRun?: () => void;
  disabled?: boolean;
}

/**
 * Format a value for console display
 */
function formatValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'function') return `[Function: ${value.name || 'anonymous'}]`;
  if (Array.isArray(value)) {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '[Array]';
    }
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '[Object]';
    }
  }
  return String(value);
}

/**
 * Get icon for console entry type
 */
function getEntryIcon(type: ConsoleEntry['type']): string {
  switch (type) {
    case 'log': return '>';
    case 'error': return '!';
    case 'warn': return '?';
    case 'info': return 'i';
    case 'result': return '=';
    default: return '>';
  }
}

export function Console({ tsCode, onRun, disabled = false }: ConsoleProps) {
  const [entries, setEntries] = useState<ConsoleEntry[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const consoleEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when entries change
  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries]);

  const handleClear = useCallback(() => {
    setEntries([]);
  }, []);

  const handleRun = useCallback(async () => {
    if (!tsCode || disabled) return;

    setIsRunning(true);
    onRun?.();

    const newEntries: ConsoleEntry[] = [
      { type: 'info', content: '--- Execution started ---', timestamp: Date.now() }
    ];

    // Store original console methods
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalInfo = console.info;

    // Override console methods to capture output
    console.log = (...args: unknown[]) => {
      newEntries.push({
        type: 'log',
        content: args.map(formatValue).join(' '),
        timestamp: Date.now()
      });
      // Update state for real-time display during async execution
      setEntries([...newEntries]);
    };

    console.error = (...args: unknown[]) => {
      newEntries.push({
        type: 'error',
        content: args.map(formatValue).join(' '),
        timestamp: Date.now()
      });
      setEntries([...newEntries]);
    };

    console.warn = (...args: unknown[]) => {
      newEntries.push({
        type: 'warn',
        content: args.map(formatValue).join(' '),
        timestamp: Date.now()
      });
      setEntries([...newEntries]);
    };

    console.info = (...args: unknown[]) => {
      newEntries.push({
        type: 'info',
        content: args.map(formatValue).join(' '),
        timestamp: Date.now()
      });
      setEntries([...newEntries]);
    };

    try {
      // Strip TypeScript types to get executable JavaScript
      let jsCode = stripTypes(tsCode);

      // Auto-await top-level function calls at the end of the code
      // This handles patterns like: functionName(); or functionName(args);
      // Transform to: await functionName(); so async functions complete properly
      jsCode = jsCode.replace(/(\n\s*)(\w+\s*\([^)]*\)\s*;)\s*$/, '$1await $2');

      // Get XION mocks for injection
      const xionMocks = getXionMocks();

      // Build async wrapper function with XION functions available
      const asyncWrapper = `
        return (async () => {
          ${jsCode}
        })();
      `;

      // Create function with XION functions as parameters
      const executeCode = new Function(
        ...Object.keys(xionMocks),
        asyncWrapper
      );

      // Execute with mocks injected
      const result = await executeCode(...Object.values(xionMocks));

      if (result !== undefined) {
        newEntries.push({
          type: 'result',
          content: `=> ${formatValue(result)}`,
          timestamp: Date.now()
        });
      }

      newEntries.push({
        type: 'info',
        content: '--- Execution completed ---',
        timestamp: Date.now()
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      newEntries.push({
        type: 'error',
        content: `Merde! Runtime Error: ${errorMessage}`,
        timestamp: Date.now()
      });
    } finally {
      // Restore original console methods
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
      console.info = originalInfo;

      setIsRunning(false);
      setEntries([...newEntries]);
    }
  }, [tsCode, disabled, onRun]);

  return (
    <div className="console-container">
      <div className="console-header">
        <span className="console-title">Console</span>
        <div className="console-actions">
          <button
            className="console-clear-btn"
            onClick={handleClear}
            disabled={entries.length === 0}
          >
            Clear
          </button>
          <button
            className="console-run-btn"
            onClick={handleRun}
            disabled={isRunning || disabled || !tsCode}
          >
            {isRunning ? 'Running...' : 'Run'}
          </button>
        </div>
      </div>
      <div className="console-output">
        {entries.length === 0 ? (
          <div className="console-placeholder">
            Click "Run" to execute the code...
          </div>
        ) : (
          entries.map((entry, index) => (
            <div key={index} className={`console-entry console-entry-${entry.type}`}>
              <span className="console-entry-icon">{getEntryIcon(entry.type)}</span>
              <span className="console-entry-content">{entry.content}</span>
            </div>
          ))
        )}
        <div ref={consoleEndRef} />
      </div>
    </div>
  );
}
